<template>
  <v-container fluid>
    <h2>
      {{ $t('settings') }}
      <span class="sub-title"> {{ user.email }}</span>
    </h2>
    <v-row>
      <v-col>
        <v-form id="user-settings-form" @submit.prevent="onSubmit" autocomplete="off">
          <v-text-field
            v-model="user.email"
            :label="$tc('email_address')"
            disabled/>
          <v-text-field
            v-model="user.commaSeperatedRoles"
            :label="$tc('role')"
            disabled/>
          <v-text-field
            v-model="profile.firstName"
            :label="$t('first_name')"
            :error-messages="firstNameErrors"/>
          <v-text-field
            v-model="profile.lastName"
            :label="$t('last_name')"
            :error-messages="lastNameErrors"/>
          <v-text-field
            v-model="profile.phone"
            :label="$t('phone_number')"
            :error-messages="phoneErrors"/>
          <v-text-field
            v-model="profile.title"
            :label="$t('job_title')"
            :error-messages="titleErrors"/>
          <v-radio-group
            v-model="homepage"
            :label="$t('set_my_home_page_as')"
            :error-messages="homepageErrors">
            <v-radio
              v-for="(route, label) in homepageRoutes"
              :key="label"
              :label="$t(label)"
              :value="route"/>
          </v-radio-group>
          <password-change-form
            ref="password-change-form"
            class="password-group"
            layout="horizontal"
            :password.sync="password"
            :current-password.sync="currentPassword"
            :require-current-password="true"
            :passwordLabel="$t('change_your_password_optional')"
            :repeatPasswordLabel="$t('reenter_password')"
            :passwordHelpText="$t('password_info')"
          />
          <v-btn class="mt-1 float-right" type="submit" color="primary" :loading="saving" @click="$gtag.event('update', {event_category:'UserSetting'})">
            <v-icon>mdi-content-save</v-icon>
            {{ $t('save_settings') }}
          </v-btn>
        </v-form>
      </v-col>
    </v-row>

  </v-container>
</template>

<script>

import AuthUtil from '@/util/AuthUtil'
import PasswordChangeForm from '@/components/shared/PasswordChangeForm'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import BrowserUtilitiesMixin from '@/components/mixins/BrowserUtilitiesMixin'
import ValidationMixin from '@/components/mixins/ValidationMixin'
import UserRecord from '@/orm/models/UserRecord'
import { required } from 'vuelidate/lib/validators'
import { isEmpty, map } from 'lodash'

export default {
  name: 'UserSettings',
  components: {
    PasswordChangeForm
  },
  mixins: [
    CurrentUserMixin,
    BrowserUtilitiesMixin,
    ValidationMixin
  ],
  validations: {
    profile: {
      firstName: {
        required
      },
      lastName: {
        required
      },
      phone: {
        required
      },
      title: {
        required
      }
    },
    homepage: {
      required
    }
  },
  data () {
    return {
      profile: {},
      homepage: '',
      password: null,
      currentPassword: null,
      saving: false,
      homepageRoutes: UserRecord.getHomePageRoutes()
    }
  },
  methods: {
    async onSubmit () {
      this.$v.$touch()

      // Password is optional, do not validate if blank
      let passwordValid = true
      if (!isEmpty(this.password)) {
        passwordValid = this.$refs['password-change-form'].validate()
      }
      if (!passwordValid || this.$v.$invalid) return false

      const json = {
        profile: this.profile,
        homepage: this.homepage
      }

      this.saving = true

      // Changing the password will invalidate the user's JWT.
      // If a password change is requested, update the password and re-auth before
      // proceeding.
      if (!isEmpty(this.password)) {
        // Force UserModule to ignore the next user update event that the API
        // pushes. This will prevent it from triggering a request using the old
        // JWT that we invalidated.
        await this.$store.dispatch('userModule/ignoreNextUserUpdate')
        // Change password
        this.user.password = this.password
        this.user.original_password = this.currentPassword
        await this.$orm('User').update(this.user.id, this.user)
          .catch(error => {
            this.saving = false
            this.handleError(error)
          })

        // Re-auth with new password
        await AuthUtil.auth(this.user.email, this.user.password)
          .catch(error => {
            this.saving = false
            this.handleError(error)
          })
      }

      // Save user settings
      this.user.updateSettings(json)
        .then(() => {
          this.$v.$reset()
          this.$refs['password-change-form'].reset()
          this.$store.dispatch('globalMessagingModule/clearFlashBanners')
          this.$store.dispatch(
            'globalMessagingModule/showSuccessFlashBanner',
            `${this.$i18n.tc('user')} ${this.$i18n.t('generic_save_success')}`
          )
        })
        .catch(error => {
          this.handleError(error)
        })
        .finally(() => {
          this.saving = false
        })
    }
  },
  computed: {
    /**
     * Return a comma seperated list of role names based on the roles assigned
     * to the user
     *
     * @return string
     */
    commaSeperatedRoles () {
      return map(this.user.roles, 'name').join(', ')
    },

    firstNameErrors () {
      return this.validationError({
        prop: this.$v.profile.firstName,
        message: this.$t('first_name_required')
      })
    },

    lastNameErrors () {
      return this.validationError({
        prop: this.$v.profile.lastName,
        message: this.$t('last_name_required')
      })
    },

    phoneErrors () {
      return this.validationError({
        prop: this.$v.profile.phone,
        message: this.$t('phone_number_required')
      })
    },

    titleErrors () {
      return this.validationError({
        prop: this.$v.profile.title,
        message: this.$t('job_title_required')
      })
    },

    homepageErrors () {
      return this.validationError({
        prop: this.$v.homepage,
        message: this.$t('home_page_required')
      })
    }
  },
  mounted () {
    this.updateBrowserTitle(this.$t('user_settings'))
    this.profile = this.user.getSetting('profile', {})
    this.homepage = this.user.getSetting('homepage')
  }
}
</script>

<style scoped>
.sub-title {
  font-size: 65%;
  color: #777777;
}

.password-group {
  background-color: #EEE;
}

/* Alter the colors of the password meter */
.password-group ::v-deep(.Password__strength-meter:after),
.password-group ::v-deep(.Password__strength-meter:before) {
  border-color: #eee;
}
.password-group ::v-deep(.Password__strength-meter) {
  background-color: #bbb;
  }
</style>
